import { ref } from 'vue';
import { defineStore } from 'pinia';

import { setAuthState } from '../composables/auth';
import { getResumeGate, dismissAutoApplyPromo, completeAutoApplyPromo } from '../api/auto-apply-promo';

export const STEPS = {
	PROMO: 'promo',
	UPLOAD: 'upload',
	LINKEDIN: 'linkedin',
	CONFIRM: 'confirm',
	ANALYSIS: 'analysis',
};

/**
 * Drives the existing-user AutoApply flow (EN-776): the promo modal, then the Resume Gate,
 * ending on `STEPS.ANALYSIS`, where Resume Analysis and Qualification (EN-777) take over.
 *
 * Whether to show the promo at all, and with which dismiss copy, is decided server-side and
 * arrives on the user payload as `auto_apply_promo`. Every write here answers with the
 * refreshed user, which replaces the cached copy.
 */
export const useAutoApplyPromoStore = defineStore('auto-apply-promo', () => {
	const { getUser, setUser } = setAuthState();

	const step = ref(null);
	const isFinalPrompt = ref(false);
	const isLoadingGate = ref(false);
	// Set when the Resume Gate lookup fails, so the promo can ask the user to try again.
	const gateError = ref(null);
	const daysSinceUpdate = ref(null);
	// The resume uploaded through the gate ({ id, uuid }); null when an existing resume is used.
	const uploadedResume = ref(null);

	const open = (promptState) => {
		isFinalPrompt.value = !!promptState?.is_final_prompt;
		step.value = STEPS.PROMO;
	};

	// Patch the cached user up front so route changes cannot reopen the promo while (or if)
	// the server write is in flight or fails; the server's answer replaces it on success.
	const hidePromoForSession = () => {
		const user = getUser();
		if (user) {
			setUser({ ...user, auto_apply_promo: { ...user.auto_apply_promo, show: false } });
		}
	};

	const dismiss = async ({ optOut }) => {
		step.value = null;
		hidePromoForSession();

		try {
			const body = await dismissAutoApplyPromo({ optOut });
			setUser(body.data);
		} catch {
			// Best-effort: the promo stays hidden this session and may return on a later login.
		}
	};

	/**
	 * The promo's secondary button: Remind Me Later, or on the final prompt "No thanks,
	 * don't remind me again".
	 */
	const dismissPromo = () => dismiss({ optOut: isFinalPrompt.value });

	// Closing the Resume Gate (or leaving it for ResumAI) counts as a Remind Me Later.
	const closeGate = () => dismiss({ optOut: false });

	const proceedToAnalysis = async (resume = null) => {
		uploadedResume.value = resume;
		step.value = STEPS.ANALYSIS;
		hidePromoForSession();

		try {
			const body = await completeAutoApplyPromo();
			setUser(body.data);
		} catch {
			// The flow carries on regardless; a failed write only lets the promo return on a later login.
		}
	};

	const getStarted = async () => {
		isLoadingGate.value = true;
		gateError.value = null;

		try {
			const { data: gate } = await getResumeGate();
			daysSinceUpdate.value = gate.days_since_update;

			if (gate.gate === 'upload') {
				step.value = STEPS.UPLOAD;
			} else if (gate.gate === 'confirm') {
				step.value = STEPS.CONFIRM;
			} else {
				await proceedToAnalysis();
			}
		} catch {
			// Stay on the promo; the button re-enables so the user can retry.
			gateError.value = 'Something went wrong. Please try again.';
		} finally {
			isLoadingGate.value = false;
		}
	};

	const showLinkedInInstructions = () => {
		step.value = STEPS.LINKEDIN;
	};

	const backToUpload = () => {
		step.value = STEPS.UPLOAD;
	};

	const uploadNewResume = () => {
		step.value = STEPS.UPLOAD;
	};

	const keepResume = () => proceedToAnalysis();

	const resumeUploaded = (resume) => proceedToAnalysis(resume);

	return {
		step,
		isFinalPrompt,
		isLoadingGate,
		gateError,
		daysSinceUpdate,
		uploadedResume,
		open,
		dismissPromo,
		closeGate,
		getStarted,
		showLinkedInInstructions,
		backToUpload,
		uploadNewResume,
		keepResume,
		resumeUploaded,
	};
});
